import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	CircularProgress,
	Alert,
	Link,
	ButtonBase,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import { Video, ChevronRight, Phone, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useGrade } from "../../../contexts/GradeContext";
import { useAcademicYear } from "../../../contexts/AcademicYearContext";
import { applyCurrentAcademicYearNotificationFilter } from "../../../utils/notifications";
import {
	filterReportSettingsForCumulative,
	getAcademicYearRange,
} from "../../../utils/academicYear";
import {
	CARD_SURFACE,
	GRADE_BUTTON_PALETTE,
} from "../../../components/teacher/module/teacherModuleStyles";

const startOfMonthISO = () => {
	const d = new Date();
	d.setDate(1);
	d.setHours(0, 0, 0, 0);
	return d.toISOString();
};

/** Sum monthly video goals for a parent grade (or ALL fallback) across settings. */
const sumTargetsForParentGrade = (settings, parentGrade) => {
	const byPeriod = {};
	(settings || []).forEach((setting) => {
		const key = `${setting.year}-${setting.month}`;
		if (!byPeriod[key]) byPeriod[key] = {};
		byPeriod[key][setting.parent_grade] =
			setting.number_of_videos_to_be_completed || 0;
	});

	let total = 0;
	let hasAny = false;
	Object.values(byPeriod).forEach((period) => {
		const value =
			parentGrade != null && period[parentGrade] != null
				? period[parentGrade]
				: period.ALL;
		if (value != null) {
			total += value;
			hasAny = true;
		}
	});
	return hasAny ? total : null;
};

const TeacherHome = () => {
	const { user, supabase } = useAuth();
	const { updateSelectedGrade } = useGrade();
	const { currentAcademicYear } = useAcademicYear();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [grades, setGrades] = useState([]);
	const [gradeProgressRows, setGradeProgressRows] = useState([]);
	const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

	const fetchDashboard = useCallback(async () => {
		if (!user?.id) return;
		if (user?.profile?.status !== "Active") {
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const { data: tg, error: tgErr } = await supabase
				.from("teacher_grades")
				.select(
					`
					grade_id,
					grades:grade_id ( id, name, parent_grade )
				`
				)
				.eq("profile_id", user.id);

			if (tgErr) throw tgErr;

			const list = (tg || [])
				.map((row) => ({
					id: row.grade_id,
					name: row.grades?.name || "Grade",
					parent_grade: row.grades?.parent_grade,
				}))
				.filter((g) => g.id);

			list.sort((a, b) =>
				String(a.name).localeCompare(String(b.name), undefined, {
					numeric: true,
				})
			);
			setGrades(list);

			// Unique game-result uploads from academic-year start (for cumulative pending).
			const ayRange = getAcademicYearRange(currentAcademicYear);
			const ayStartISO = ayRange.start.toISOString();
			const monthStartISO = startOfMonthISO();

			const { data: uploadRows, error: uploadErr } = await supabase
				.from("game_results")
				.select("content_id, grade_id, created_at")
				.eq("teacher_id", user.id)
				.eq("academic_year", currentAcademicYear)
				.gte("created_at", ayStartISO);

			if (uploadErr) throw uploadErr;

			const completedThisMonthByGradeId = {};
			const completedCumulativeByGradeId = {};
			(uploadRows || []).forEach((row) => {
				if (!row.content_id || !row.grade_id) return;

				if (!completedCumulativeByGradeId[row.grade_id]) {
					completedCumulativeByGradeId[row.grade_id] = new Set();
				}
				completedCumulativeByGradeId[row.grade_id].add(row.content_id);

				if (row.created_at >= monthStartISO) {
					if (!completedThisMonthByGradeId[row.grade_id]) {
						completedThisMonthByGradeId[row.grade_id] = new Set();
					}
					completedThisMonthByGradeId[row.grade_id].add(row.content_id);
				}
			});

			const parentIds = [
				...new Set(list.map((g) => g.parent_grade).filter(Boolean)),
			];
			const now = new Date();
			const month = now.getMonth() + 1;
			const year = now.getFullYear();

			let currentMonthSettings = [];
			let cumulativeSettings = [];
			if (parentIds.length > 0) {
				const parentGradesToFetch = [...parentIds, "ALL"];
				const { data: settings, error: rsErr } = await supabase
					.from("report_settings")
					.select(
						"parent_grade, month, year, status, number_of_videos_to_be_completed"
					)
					.eq("status", true)
					.in("parent_grade", parentGradesToFetch);

				if (!rsErr && settings?.length) {
					currentMonthSettings = settings.filter(
						(s) => s.month === month && s.year === year
					);
					cumulativeSettings = filterReportSettingsForCumulative(
						settings,
						month,
						year,
						currentAcademicYear
					);
				}
			}

			const settingsByParentGrade = {};
			currentMonthSettings.forEach((setting) => {
				settingsByParentGrade[setting.parent_grade] =
					setting.number_of_videos_to_be_completed || 0;
			});

			const progressRows = list.map((grade) => {
				const targetFromParent =
					grade.parent_grade != null
						? settingsByParentGrade[grade.parent_grade]
						: undefined;
				const target =
					targetFromParent ?? settingsByParentGrade.ALL ?? null;

				const cumulativeTarget = sumTargetsForParentGrade(
					cumulativeSettings,
					grade.parent_grade
				);
				const completed = completedThisMonthByGradeId[grade.id]?.size ?? 0;
				const cumulativeCompleted =
					completedCumulativeByGradeId[grade.id]?.size ?? 0;
				// Carry unfinished videos from earlier months in the academic year.
				const pending =
					cumulativeTarget != null
						? Math.max(0, cumulativeTarget - cumulativeCompleted)
						: null;

				return {
					gradeId: grade.id,
					gradeName: grade.name,
					target,
					cumulativeTarget,
					completed,
					pending,
				};
			});
			setGradeProgressRows(progressRows);

			const { count: unreadNotifCount, error: nErr } = await applyCurrentAcademicYearNotificationFilter(
				supabase
					.from("notifications")
					.select("id", { count: "exact", head: true })
					.eq("recipient_id", user.id)
					.or("is_read.is.null,is_read.eq.false"),
				currentAcademicYear
			);

			if (nErr) {
				console.error("TeacherHome: unread notifications count", nErr);
				setUnreadNotificationCount(0);
			} else {
				setUnreadNotificationCount(unreadNotifCount ?? 0);
			}
		} catch (e) {
			console.error(e);
			setError(e.message || "Could not load teacher home");
			setUnreadNotificationCount(0);
		} finally {
			setLoading(false);
		}
	}, [user?.id, user?.profile?.status, supabase, currentAcademicYear]);

	useEffect(() => {
		if (!user?.id) return;
		if (user?.profile?.status !== "Active") {
			setLoading(false);
			return;
		}
		fetchDashboard();
	}, [fetchDashboard, user?.id, user?.profile?.status]);

	const handlePickGrade = (gradeId) => {
		updateSelectedGrade(gradeId);
		navigate(`/teacher/topics/${gradeId}`);
	};

	const currentMonthLabel = new Date().toLocaleString("en-IN", {
		month: "long",
		year: "numeric",
	});

	const progressTotals = useMemo(() => {
		const withTarget = gradeProgressRows.filter((row) => row.target != null);
		const withCumulative = gradeProgressRows.filter(
			(row) => row.cumulativeTarget != null
		);
		const totalTarget = withTarget.reduce((sum, row) => sum + row.target, 0);
		const totalCumulativeTarget = withCumulative.reduce(
			(sum, row) => sum + row.cumulativeTarget,
			0
		);
		const totalCompleted = gradeProgressRows.reduce(
			(sum, row) => sum + row.completed,
			0
		);
		const totalPending =
			withCumulative.length > 0
				? withCumulative.reduce((sum, row) => sum + (row.pending ?? 0), 0)
				: null;

		return {
			totalTarget,
			totalCumulativeTarget,
			totalCompleted,
			totalPending,
			withTarget,
			withCumulative,
		};
	}, [gradeProgressRows]);

	/**
	 * One button per parent band (Grade 1, Grade 2, …) from Master Data `parent_grade`.
	 * Multiple class sections (1A, 1B, …) collapse to a single button; navigation uses
	 * the first assigned class under that parent (by name) so existing routes keep working.
	 */
	const parentGradeButtons = useMemo(() => {
		const sorted = [...grades].sort((a, b) =>
			String(a.name).localeCompare(String(b.name), undefined, { numeric: true })
		);
		const byParent = new Map();
		const noParent = [];
		for (const g of sorted) {
			const p = g.parent_grade?.trim();
			if (p) {
				if (!byParent.has(p)) byParent.set(p, []);
				byParent.get(p).push(g);
			} else {
				noParent.push(g);
			}
		}
		const parentOrder = [...byParent.keys()].sort((a, b) =>
			String(a).localeCompare(String(b), undefined, { numeric: true })
		);
		let paletteIndex = 0;
		const buttons = [];
		for (const parent of parentOrder) {
			const children = byParent.get(parent);
			const first = children[0];
			buttons.push({
				key: `parent:${parent}`,
				label: parent,
				gradeId: first.id,
				paletteIndex: paletteIndex++,
			});
		}
		for (const g of noParent) {
			buttons.push({
				key: `class:${g.id}`,
				label: g.name,
				gradeId: g.id,
				paletteIndex: paletteIndex++,
			});
		}
		return buttons;
	}, [grades]);

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress size={36} sx={{ color: "#16a34a" }} />
			</Box>
		);
	}

	const profileStatus = user?.profile?.status;
	const isActiveTeacher = profileStatus === "Active";

	if (!isActiveTeacher) {
		if (!user?.profile) {
			return (
				<Box>
					<Alert severity="warning" sx={{ mb: 2 }}>
						We could not load your profile. Please try refreshing the page or
						contact support.
					</Alert>
				</Box>
			);
		}
		if (profileStatus === "Pending Approval") {
			return (
				<Box>
					<Alert severity="warning" sx={{ mb: 2 }}>
						Your account is pending administrator approval. You will be able to
						access courses, videos, and scheduling once your account is approved.
					</Alert>
				</Box>
			);
		}
		if (profileStatus === "Inactive") {
			return (
				<Box>
					<Alert severity="error" sx={{ mb: 2 }}>
						Your account has been deactivated. Please contact support for
						assistance.
					</Alert>
				</Box>
			);
		}
		return (
			<Box>
				<Alert severity="warning" sx={{ mb: 2 }}>
					Your account is not active yet. Please contact your administrator.
				</Alert>
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{unreadNotificationCount > 0 && (
				<ButtonBase
					focusRipple
					onClick={() => navigate("/my-notifications")}
					aria-label={`View notifications. ${unreadNotificationCount} unread.`}
					sx={{
						...CARD_SURFACE,
						p: { xs: 1.75, sm: 2 },
						mb: 2,
						width: "100%",
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "flex-start",
						gap: 1.5,
						textAlign: "left",
						borderRadius: 3,
						"&:hover": {
							boxShadow: "0 10px 36px rgba(15, 23, 42, 0.14)",
						},
					}}>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1.25,
							flex: 1,
							minWidth: 0,
						}}>
						<Box
							sx={{
								mt: 0.25,
								color: "#6366f1",
								flexShrink: 0,
								display: "flex",
							}}
							aria-hidden>
							<Bell size={22} strokeWidth={2.25} />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								sx={{
									fontWeight: 800,
									color: "#1e3a5f",
									fontSize: "1rem",
									mb: 0.25,
								}}>
								You have unread notifications
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{unreadNotificationCount} new{" "}
								{unreadNotificationCount === 1 ? "message" : "messages"} in
								your inbox.
							</Typography>
						</Box>
					</Box>
					<Box
						sx={{
							color: "#6366f1",
							flexShrink: 0,
							display: "flex",
							alignSelf: "center",
						}}
						aria-hidden>
						<ChevronRight size={22} strokeWidth={2.5} />
					</Box>
				</ButtonBase>
			)}

			<Box sx={{ ...CARD_SURFACE, p: { xs: 2, sm: 3 }, mb: 2 }}>
				<Typography
					sx={{
						fontWeight: 700,
						color: "#64748b",
						fontSize: { xs: "0.9rem", sm: "0.95rem" },
						mb: 0.75,
					}}>
					Funmath Videos
				</Typography>
				<Typography
					sx={{
						fontWeight: 800,
						color: "#1e3a5f",
						fontSize: { xs: "1.35rem", sm: "1.5rem" },
						mb: 2.5,
					}}>
					Start with your class
				</Typography>

				{grades.length === 0 ? (
					<Typography color="text.secondary">
						No grades are assigned to your account yet. Please contact your
						administrator.
					</Typography>
				) : (
					<Grid container spacing={1.5}>
						{parentGradeButtons.map((btn) => {
							const pal =
								GRADE_BUTTON_PALETTE[
									btn.paletteIndex % GRADE_BUTTON_PALETTE.length
								];
							return (
								<Grid item xs={6} sm={4} key={btn.key}>
									<Button
										fullWidth
										onClick={() => handlePickGrade(btn.gradeId)}
										sx={{
											py: 2,
											minHeight: 72,
											borderRadius: 3,
											textTransform: "none",
											fontWeight: 800,
											fontSize: "1.05rem",
											color: "#fff",
											backgroundColor: pal.bg,
											border: "none",
											boxShadow: `0 6px 0 ${pal.shadow}, 0 8px 20px rgba(0,0,0,0.15)`,
											"&:hover": {
												backgroundColor: pal.bg,
												filter: "brightness(1.05)",
												boxShadow: `0 4px 0 ${pal.shadow}, 0 6px 16px rgba(0,0,0,0.18)`,
												transform: "translateY(2px)",
											},
										}}>
										{btn.label}
									</Button>
								</Grid>
							);
						})}
					</Grid>
				)}
			</Box>

			<Box sx={{ ...CARD_SURFACE, p: { xs: 2, sm: 3 } }}>
				<Typography
					sx={{
						fontWeight: 800,
						color: "#1e3a5f",
						fontSize: { xs: "1.15rem", sm: "1.25rem" },
						mb: 1.5,
					}}>
					Your Progress
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mb: 1.5 }}>
					Implementation videos for {currentMonthLabel}. Pending includes
					unfinished videos from earlier months this academic year.
				</Typography>

				{grades.length === 0 ? (
					<Typography color="text.secondary">
						No grades are assigned to your account yet.
					</Typography>
				) : (
					<TableContainer
						component={Paper}
						variant="outlined"
						sx={{ borderRadius: 2 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
									<TableCell align="center" sx={{ fontWeight: 700 }}>
										Goal for month
									</TableCell>
									<TableCell align="center" sx={{ fontWeight: 700 }}>
										To achieve 100%
									</TableCell>
									<TableCell align="center" sx={{ fontWeight: 700 }}>
										Completed
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{gradeProgressRows.map((row) => (
									<TableRow key={row.gradeId}>
										<TableCell sx={{ fontWeight: 600 }}>
											{row.gradeName}
										</TableCell>
										<TableCell align="center">
											{row.target != null ? row.target : "—"}
										</TableCell>
										<TableCell
											align="center"
											sx={{
												color:
													row.pending != null && row.pending > 0
														? "#d97706"
														: "inherit",
												fontWeight:
													row.pending != null && row.pending > 0 ? 700 : 400,
											}}>
											{row.pending != null ? row.pending : "—"}
										</TableCell>
										<TableCell
											align="center"
											sx={{
												color: row.completed > 0 ? "#16a34a" : "inherit",
												fontWeight: row.completed > 0 ? 700 : 400,
											}}>
											{row.completed}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{progressTotals.withCumulative.length > 0 ? (
					<Typography
						sx={{
							mt: 1.5,
							color: "#16a34a",
							fontWeight: 600,
							fontSize: "0.95rem",
						}}>
						{progressTotals.totalPending > 0
							? `You're almost there — ${progressTotals.totalPending} more video${
									progressTotals.totalPending === 1 ? "" : "s"
							  } to catch up (including earlier months). Year-to-date goal: ${
									progressTotals.totalCumulativeTarget
							  }.`
							: progressTotals.totalCumulativeTarget > 0
							? "You've caught up on your video goals so far. Great work!"
							: "Keep going!"}
					</Typography>
				) : (
					grades.length > 0 && (
						<Typography
							sx={{ mt: 1.5, color: "#64748b", fontSize: "0.95rem" }}>
							Monthly targets appear when your program sets report goals for
							your grades.
						</Typography>
					)
				)}

				<Button
					fullWidth
					variant="contained"
					startIcon={<Video size={22} strokeWidth={2.5} />}
					endIcon={<ChevronRight size={18} />}
					onClick={() => navigate("/game-results-upload")}
					sx={{
						mt: 2.5,
						py: 1.5,
						borderRadius: 2.5,
						textTransform: "none",
						fontWeight: 800,
						fontSize: "1rem",
						background: "linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)",
						boxShadow: "0 6px 0 #1e40af, 0 10px 24px rgba(37, 99, 235, 0.35)",
						"&:hover": {
							background: "linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)",
							boxShadow: "0 4px 0 #1e40af, 0 8px 20px rgba(37, 99, 235, 0.4)",
							transform: "translateY(2px)",
						},
					}}>
					Upload &amp; Complete
				</Button>
			</Box>

			<Box
				sx={{
					mt: 3,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1.25,
					py: 1,
				}}>
				<img
					src="/logo.png"
					alt="Funmiyo"
					style={{ height: 40, width: "auto", objectFit: "contain" }}
				/>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
					}}>
					<Phone size={18} strokeWidth={2.25} color="#64748b" aria-hidden />
					<Link
						href="tel:+919946776774"
						underline="hover"
						variant="body2"
						sx={{ fontWeight: 600, color: "primary.main" }}>
						+91 99467 76774
					</Link>
				</Box>
			</Box>
		</Box>
	);
};

export default TeacherHome;
